/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  CustomFieldRequestEntity,
  CustomFieldRequestEntity$inboundSchema,
  CustomFieldRequestEntity$Outbound,
  CustomFieldRequestEntity$outboundSchema,
} from "./customfieldrequestentity.js";
import {
  ProductCurrency,
  ProductCurrency$inboundSchema,
  ProductCurrency$outboundSchema,
} from "./productcurrency.js";
import {
  ProductRequestBillingPeriod,
  ProductRequestBillingPeriod$inboundSchema,
  ProductRequestBillingPeriod$outboundSchema,
} from "./productrequestbillingperiod.js";
import {
  ProductRequestBillingType,
  ProductRequestBillingType$inboundSchema,
  ProductRequestBillingType$outboundSchema,
} from "./productrequestbillingtype.js";
import {
  TaxCategory,
  TaxCategory$inboundSchema,
  TaxCategory$outboundSchema,
} from "./taxcategory.js";
import {
  TaxMode,
  TaxMode$inboundSchema,
  TaxMode$outboundSchema,
} from "./taxmode.js";

export type CreateProductRequestEntity = {
  /**
   * Name of the product
   */
  name: string;
  /**
   * Description of the product
   */
  description?: string | undefined;
  /**
   * URL of the product image
   */
  imageUrl?: string | undefined;
  /**
   * The price of the product in cents
   */
  price: number;
  /**
   * Three-letter ISO currency code, in uppercase. Must be a supported currency.
   */
  currency: ProductCurrency;
  /**
   * Indicates the billing method for the customer. It can either be a `recurring` billing cycle or a `onetime` payment.
   */
  billingType: ProductRequestBillingType;
  /**
   * Billing period, required if billing_type is recurring
   */
  billingPeriod?: ProductRequestBillingPeriod | undefined;
  /**
   * Specifies the tax calculation mode for the transaction. If set to "inclusive," the tax is included in the price. If set to "exclusive," the tax is added on top of the price.
   */
  taxMode?: TaxMode | undefined;
  /**
   * Categorizes the type of product or service for tax purposes. This helps determine the applicable tax rules based on the nature of the item or service.
   */
  taxCategory?: TaxCategory | undefined;
  /**
   * The URL to which the user will be redirected after successfull payment.
   */
  defaultSuccessUrl?: string | undefined;
  /**
   * Collect additional information from your customer using custom fields during checkout. Up to 3 fields are supported.
   */
  customFields?: Array<CustomFieldRequestEntity> | undefined;
  /**
   * DEPRECATED: Use `custom_fields` instead. Collect additional information from your customer using custom fields during checkout. Up to 3 fields are supported.
   *
   * @deprecated field: This will be removed in a future release, please migrate away from it as soon as possible.
   */
  customField?: Array<CustomFieldRequestEntity> | undefined;
  /**
   * Enable abandoned cart recovery for this product
   */
  abandonedCartRecoveryEnabled?: boolean | undefined;
};

/** @internal */
export const CreateProductRequestEntity$inboundSchema: z.ZodType<
  CreateProductRequestEntity,
  z.ZodTypeDef,
  unknown
> = z.object({
  name: z.string(),
  description: z.string().optional(),
  image_url: z.string().optional(),
  price: z.number().int(),
  currency: ProductCurrency$inboundSchema,
  billing_type: ProductRequestBillingType$inboundSchema,
  billing_period: ProductRequestBillingPeriod$inboundSchema.optional(),
  tax_mode: TaxMode$inboundSchema.optional(),
  tax_category: TaxCategory$inboundSchema.optional(),
  default_success_url: z.string().optional(),
  custom_fields: z.array(CustomFieldRequestEntity$inboundSchema).optional(),
  custom_field: z.array(CustomFieldRequestEntity$inboundSchema).optional(),
  abandoned_cart_recovery_enabled: z.boolean().default(false),
}).transform((v) => {
  return remap$(v, {
    "image_url": "imageUrl",
    "billing_type": "billingType",
    "billing_period": "billingPeriod",
    "tax_mode": "taxMode",
    "tax_category": "taxCategory",
    "default_success_url": "defaultSuccessUrl",
    "custom_fields": "customFields",
    "custom_field": "customField",
    "abandoned_cart_recovery_enabled": "abandonedCartRecoveryEnabled",
  });
});
/** @internal */
export type CreateProductRequestEntity$Outbound = {
  name: string;
  description?: string | undefined;
  image_url?: string | undefined;
  price: number;
  currency: string;
  billing_type: string;
  billing_period?: string | undefined;
  tax_mode?: string | undefined;
  tax_category?: string | undefined;
  default_success_url?: string | undefined;
  custom_fields?: Array<CustomFieldRequestEntity$Outbound> | undefined;
  custom_field?: Array<CustomFieldRequestEntity$Outbound> | undefined;
  abandoned_cart_recovery_enabled: boolean;
};

/** @internal */
export const CreateProductRequestEntity$outboundSchema: z.ZodType<
  CreateProductRequestEntity$Outbound,
  z.ZodTypeDef,
  CreateProductRequestEntity
> = z.object({
  name: z.string(),
  description: z.string().optional(),
  imageUrl: z.string().optional(),
  price: z.number().int(),
  currency: ProductCurrency$outboundSchema,
  billingType: ProductRequestBillingType$outboundSchema,
  billingPeriod: ProductRequestBillingPeriod$outboundSchema.optional(),
  taxMode: TaxMode$outboundSchema.optional(),
  taxCategory: TaxCategory$outboundSchema.optional(),
  defaultSuccessUrl: z.string().optional(),
  customFields: z.array(CustomFieldRequestEntity$outboundSchema).optional(),
  customField: z.array(CustomFieldRequestEntity$outboundSchema).optional(),
  abandonedCartRecoveryEnabled: z.boolean().default(false),
}).transform((v) => {
  return remap$(v, {
    imageUrl: "image_url",
    billingType: "billing_type",
    billingPeriod: "billing_period",
    taxMode: "tax_mode",
    taxCategory: "tax_category",
    defaultSuccessUrl: "default_success_url",
    customFields: "custom_fields",
    customField: "custom_field",
    abandonedCartRecoveryEnabled: "abandoned_cart_recovery_enabled",
  });
});

export function createProductRequestEntityToJSON(
  createProductRequestEntity: CreateProductRequestEntity,
): string {
  return JSON.stringify(
    CreateProductRequestEntity$outboundSchema.parse(createProductRequestEntity),
  );
}
export function createProductRequestEntityFromJSON(
  jsonString: string,
): SafeParseResult<CreateProductRequestEntity, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => CreateProductRequestEntity$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'CreateProductRequestEntity' from JSON`,
  );
}
